'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useGetCreatedMarketsAndProposalsInfinite, useMarketConfigCatalog } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { ProposalFeedListHeader } from '@/components/launchpad/proposal-feed-list-header';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { logger } from '@/lib/telemetry/logger';
import { CreatedMarketRow } from './created-market-row';
import { CreatedProposalRow } from './created-proposal-row';
import { useCreatorBackerRewards } from './use-creator-backer-rewards';
export interface CreatedListProps {
  userId: string;
  sortOrder: 'asc' | 'desc';
}
export function CreatedMarkets(t0) {
  const $ = _c(15);
  const {
    userId,
    sortOrder,
    status,
    creatorName
  } = t0;
  let t1;
  if ($[0] !== sortOrder || $[1] !== status || $[2] !== userId) {
    t1 = {
      creatorUserId: userId,
      group: "markets",
      status,
      sortOrder
    };
    $[0] = sortOrder;
    $[1] = status;
    $[2] = userId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const query = useGetCreatedMarketsAndProposalsInfinite(t1);
  const {
    backerRewardsFor,
    observeBackerRewardsRow
  } = useCreatorBackerRewards();
  let t2;
  if ($[4] !== backerRewardsFor || $[5] !== creatorName || $[6] !== observeBackerRewardsRow) {
    t2 = t3 => {
      const {
        id,
        ref
      } = t3;
      return <li key={id} ref={ref}><CreatedMarketRow pubkey={id} creatorName={creatorName} backerRewards={backerRewardsFor(id)} backerRewardsRef={observeBackerRewardsRow(id)} /></li>;
    };
    $[4] = backerRewardsFor;
    $[5] = creatorName;
    $[6] = observeBackerRewardsRow;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== query.Items || $[9] !== query.queryKey || $[10] !== t2) {
    t3 = <ul className="flex flex-col gap-2"><query.Items queryKey={query.queryKey}>{t2}</query.Items></ul>;
    $[8] = query.Items;
    $[9] = query.queryKey;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== query || $[13] !== t3) {
    t4 = <CreatedListState query={query} group="markets">{t3}</CreatedListState>;
    $[12] = query;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export function CreatedProposals(t0) {
  const $ = _c(26);
  const {
    userId,
    sortOrder
  } = t0;
  let t1;
  if ($[0] !== sortOrder || $[1] !== userId) {
    t1 = {
      creatorUserId: userId,
      group: "proposals",
      sortOrder
    };
    $[0] = sortOrder;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useGetCreatedMarketsAndProposalsInfinite(t1);
  const catalog = useMarketConfigCatalog();
  let t2;
  if ($[3] !== query.rows) {
    t2 = new Map(query.rows.map(_temp));
    $[3] = query.rows;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const rows = t2;
  const t3 = query.isError || catalog.status === "unavailable";
  let t4;
  if ($[5] !== catalog || $[6] !== query) {
    t4 = () => {
      query.refetch();
      catalog.retry();
    };
    $[5] = catalog;
    $[6] = query;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== query.fetchNextPage || $[9] !== query.hasNextPage || $[10] !== query.isFetchingNextPage || $[11] !== query.isPending || $[12] !== query.rows || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      isPending: query.isPending,
      rows: query.rows,
      hasNextPage: query.hasNextPage,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage,
      isError: t3,
      refetch: t4
    };
    $[8] = query.fetchNextPage;
    $[9] = query.hasNextPage;
    $[10] = query.isFetchingNextPage;
    $[11] = query.isPending;
    $[12] = query.rows;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ProposalFeedListHeader />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== rows) {
    t7 = t8 => {
      const {
        id,
        ref
      } = t8;
      return <li key={id} ref={ref} className="min-w-0"><CreatedProposalRow pubkey={id} snapshot={rows.get(id)} /></li>;
    };
    $[17] = rows;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== query.Items || $[20] !== query.queryKey || $[21] !== t7) {
    t8 = <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-1"><query.Items queryKey={query.queryKey}>{t7}</query.Items></ul>;
    $[19] = query.Items;
    $[20] = query.queryKey;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t5 || $[24] !== t8) {
    t9 = <CreatedListState query={t5} group="proposals">{t6}{t8}</CreatedListState>;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp(row) {
  return [row.pubkey, row];
}
interface ListState {
  isPending: boolean;
  isError: boolean;
  rows: readonly object[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
  fetchNextPage: () => Promise<void>;
}
function CreatedListState(t0) {
  const $ = _c(32);
  const {
    query,
    group,
    children
  } = t0;
  const t = useTranslations("profile.created");
  let t1;
  let t2;
  if ($[0] !== group || $[1] !== query.isError) {
    t1 = () => {
      if (query.isError) {
        logger.warn("portfolio.created_query_failed", {
          group
        });
      }
    };
    t2 = [query.isError, group];
    $[0] = group;
    $[1] = query.isError;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (query.isPending) {
    let t3;
    if ($[4] !== t) {
      t3 = t("loading");
      $[4] = t;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-20 w-full rounded-card" />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t3) {
      t5 = <output aria-label={t3}>{t4}</output>;
      $[7] = t3;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (query.isError) {
    let t3;
    if ($[9] !== t) {
      t3 = t("error");
      $[9] = t;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== query) {
      t4 = () => {
        query.refetch();
      };
      $[11] = query;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t) {
      t5 = t("retry");
      $[13] = t;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t4 || $[16] !== t5) {
      t6 = <AnimatedButton variant="secondary" onClick={t4}>{t5}</AnimatedButton>;
      $[15] = t4;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t3 || $[19] !== t6) {
      t7 = <EmptyState size="compact" title={t3} action={t6} />;
      $[18] = t3;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  if (query.rows.length === 0) {
    let t3;
    if ($[21] !== group || $[22] !== t) {
      t3 = group === "markets" ? t("emptyMarkets") : t("emptyProposals");
      $[21] = group;
      $[22] = t;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    let t4;
    if ($[24] !== t3) {
      t4 = <EmptyState size="compact" title={t3} />;
      $[24] = t3;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    return t4;
  }
  let t3;
  if ($[26] !== query || $[27] !== t) {
    t3 = query.hasNextPage ? <div className="flex justify-center pt-3"><AnimatedButton variant="secondary" disabled={query.isFetchingNextPage} loading={query.isFetchingNextPage} onClick={() => {
        query.fetchNextPage();
      }}>{query.isFetchingNextPage ? t("loading") : t("loadMore")}</AnimatedButton></div> : null;
    $[26] = query;
    $[27] = t;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  let t4;
  if ($[29] !== children || $[30] !== t3) {
    t4 = <>{children}{t3}</>;
    $[29] = children;
    $[30] = t3;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  return t4;
}